import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ViewMode } from '../types/AppPanel';

interface AgentViewContextType {
  /** Whether we're currently on the Agent screen */
  isAgentScreen: boolean;
  /** Current mobile view mode ('chat' | 'preview') */
  mobileView: 'chat' | 'preview';
  /** Current app view mode (preview, database, cloud-code, etc.) */
  mobileAppViewMode: ViewMode;
  /** Set agent screen state */
  setIsAgentScreen: (value: boolean) => void;
  /** Set mobile view */
  setMobileView: (value: 'chat' | 'preview') => void;
  /** Set mobile app view mode */
  setMobileAppViewMode: (value: ViewMode) => void;
}

const AgentViewContext = createContext<AgentViewContextType | undefined>(undefined);

interface AgentViewProviderProps {
  children: ReactNode;
}

export function AgentViewProvider({ children }: AgentViewProviderProps) {
  const [isAgentScreen, setIsAgentScreen] = useState(false);
  const [mobileView, setMobileView] = useState<'chat' | 'preview'>('chat');
  const [mobileAppViewMode, setMobileAppViewMode] = useState<ViewMode>('preview');

  return (
    <AgentViewContext.Provider
      value={{
        isAgentScreen,
        mobileView,
        mobileAppViewMode,
        setIsAgentScreen,
        setMobileView,
        setMobileAppViewMode,
      }}
    >
      {children}
    </AgentViewContext.Provider>
  );
}

export function useAgentView() {
  const context = useContext(AgentViewContext);
  if (context === undefined) {
    throw new Error('useAgentView must be used within an AgentViewProvider');
  }
  return context;
}

/**
 * Hook to check if navbar should be hidden
 * Returns true when on Agent screen, in mobile, and viewing Preview or Database
 */
export function useShouldHideNavbar(isMobile: boolean): boolean {
  const context = useContext(AgentViewContext);
  
  if (!context) {
    return false;
  }

  const { isAgentScreen, mobileView, mobileAppViewMode } = context;

  // Hide navbar only when:
  // 1. On Agent screen
  // 2. On mobile
  // 3. In preview mode (not chat)
  // 4. Viewing 'preview' or 'database'
  return (
    isMobile &&
    isAgentScreen &&
    mobileView === 'preview' &&
    (mobileAppViewMode === 'preview' || mobileAppViewMode === 'database')
  );
}

export default AgentViewContext;

